import { PlusCircle } from "react-bootstrap-icons";
import { AsyncButton } from "../../controls";
import { getFromDataPath } from "../../utility";
import { ReferencedOperationRelationship } from "../ars_ldm";
import { useARSContext } from "../contexts";
import { PATCH_ARS_STATE } from "../contexts/interface";
import { ARSText } from "../inputs/ARSText";
import { isOperation } from "../typeGuards/isOperation";
import { NamedObjectDisplay } from "./NamedObjectDisplay";
import { ReferencedOperationRelationshipControl } from "./ReferencedOperationRelationshipControl";
import { RemoveArsItemButton } from "./RemoveArsItemButton";

/**
 * Props for the OperationControl component.
 */
export interface OperationControlProps {
  /** RFC 6901 JSON Pointer path to the Operation in ARSContext state. */
  dataPath: string;
  /** Disabled state. */
  disabled?: boolean;
  /** Sibling count */
  siblingCount: number;
}

/**
 * A component for managing Operation entities.
 *
 * This component provides:
 * - NamedObjectDisplay for editing name, description, and label
 * - Result pattern input
 * - Management of nested ReferencedOperationRelationship entities
 * - Remove button for deleting the operation
 *
 * @param props - Component props.
 * @param props.dataPath - RFC 6901 JSON Pointer path to the Operation in ARSContext state.
 * @param props.disabled - Optional flag to disable editing controls.
 * @param props.siblingCount - Number of sibling items
 * @returns A React functional component rendering the operation editor.
 */
export const OperationControl = ({
  dataPath,
  disabled = false,
  siblingCount,
}: OperationControlProps): React.ReactElement => {
  const { state, dispatch } = useARSContext();

  const operation = getFromDataPath(dataPath, state, isOperation);
  const relationships = operation?.referencedOperationRelationships ?? [];

  /**
   * Handles adding a new referenced operation relationship.
   */
  const handleAddRelationship = (): void => {
    /* istanbul ignore else -- Disabled buttons cannot invoke this handler from the UI */
    if (!disabled && operation) {
      const relationshipCount = relationships.length;
      const newRelationship: ReferencedOperationRelationship = {
        id: crypto.randomUUID(),
        referencedOperationRole: {
          controlledTerm: "",
        },
        operationId: "",
        analysisId: "",
        description: "",
      };

      dispatch({
        operation: PATCH_ARS_STATE,
        dataPath: `${dataPath}/referencedOperationRelationships/${relationshipCount}`,
        value: newRelationship,
      });
    }
  };

  const idRoute = dataPath.replace(/\//g, "-");

  return (
    <div className="border rounded p-3 mb-3 position-relative">
      <div className="position-absolute top-0 end-0 m-2">
        <RemoveArsItemButton
          dataPath={dataPath}
          disabled={disabled}
          siblingCount={siblingCount}
        />
      </div>

      <NamedObjectDisplay
        dataPath={dataPath}
        canRename
        disabled={disabled}
      />

      <ARSText
        dataPath={`${dataPath}/resultPattern`}
        id={`${idRoute}-result-pattern`}
        label="Result pattern"
        labelWidth={2}
        placeholder="Enter result pattern"
        disabled={disabled}
      />

      {relationships.map((relationship, index) => (
        <ReferencedOperationRelationshipControl
          key={relationship.id}
          dataPath={`${dataPath}/referencedOperationRelationships/${index}`}
          disabled={disabled}
          siblingCount={relationships.length}
        />
      ))}

      <AsyncButton
        id={`${idRoute}-add-relationship`}
        onClick={handleAddRelationship}
        className="d-flex align-items-center btn-sm btn-success"
        disabled={disabled}
      >
        <span className="me-2">Add relationship</span>
        <PlusCircle />
      </AsyncButton>
    </div>
  );
};

OperationControl.displayName = "OperationControl";
